import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Container,
  Card,
  Accordion,
  Form,
  Button,
  Alert,
  Nav,
  Tab,
  Spinner
} from "react-bootstrap";
import { 
  FaQuestionCircle, 
  FaBook, 
  FaExclamationTriangle, 
  FaPhoneAlt, 
  FaLine, 
  FaFacebook, 
  FaGlobe,
  FaPaperPlane
} from "react-icons/fa";
import { helpCenterService } from "../../services/api";

const HelpCenter = () => {
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState("success");
  const [supportForm, setSupportForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userGuides, setUserGuides] = useState([]);
  const [troubleshootingGuides, setTroubleshootingGuides] = useState([]);
  const [contactInfo, setContactInfo] = useState(null);
  const [supportCategories, setSupportCategories] = useState([]);
  
  const handleSupportFormChange = (e) => {
    const { name, value } = e.target;
    setSupportForm({
      ...supportForm,
      [name]: value
    });
  };
  
  useEffect(() => {
    const loadHelpCenterData = async () => {
      try {
        setLoading(true);
        
        const [guidesResponse, troubleshootingResponse, settingsResponse] = await Promise.all([
          helpCenterService.getUserGuides({ language: 'th' }),
          helpCenterService.getTroubleshootingGuides({ language: 'th' }),
          helpCenterService.getSettings()
        ]);
        
        if (guidesResponse.success && guidesResponse.data.guides) {
          setUserGuides(guidesResponse.data.guides);
        }
        
        if (troubleshootingResponse.success && troubleshootingResponse.data.guides) {
          setTroubleshootingGuides(troubleshootingResponse.data.guides);
        }
        
        if (settingsResponse.success && settingsResponse.data) {
          setContactInfo(settingsResponse.data.contact);
          setSupportCategories(settingsResponse.data.supportCategories || []);
        }
      } catch (error) {
        console.error('Failed to load help center data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadHelpCenterData();
  }, []);
  
  const handleSupportSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      const response = await helpCenterService.submitSupportRequest({
        name: supportForm.name,
        email: supportForm.email,
        subject: supportForm.subject,
        message: supportForm.message
      });
      
      if (response.success) {
        setAlertVariant("success");
        setAlertMessage(`ส่งคำขอความช่วยเหลือเรียบร้อย Ticket ID: ${response.data.ticketId}`);
        setShowAlert(true);
        
        setSupportForm({
          name: "",
          email: "",
          subject: "",
          message: ""
        });
      }
    } catch (error) {
      setAlertVariant("danger");
      setAlertMessage(error.response?.data?.error?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
      setShowAlert(true);
    } finally {
      setSubmitting(false);
      setTimeout(() => {
        setShowAlert(false);
      }, 5000);
    }
  };

  return (
    <>
      <div id="content-page" className="content-inner">
        <Container>
          <Row className="row-eq-height">
            <Col md="12">
              <Row className="mb-5">
                <Col md="12">
                  <div className="card-header d-flex justify-content-between">
                    <div className="header-title">
                      <h4 className="card-title">ศูนย์ช่วยเหลือ</h4>
                    </div>
                  </div>
                </Col>
              </Row>
              
              {showAlert && (
                <Alert variant={alertVariant} onClose={() => setShowAlert(false)} dismissible>
                  {alertMessage}
                </Alert>
              )}
              
              <Tab.Container defaultActiveKey="userGuide">
                <Row>
                  <Col lg="3" md="4" className="mb-4">
                    <Card>
                      <Card.Body className="p-0">
                        <Nav variant="pills" className="flex-column">
                          <Nav.Item>
                            <Nav.Link eventKey="userGuide" className="d-flex align-items-center">
                              <FaBook className="me-2" /> คู่มือการใช้งาน
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link eventKey="troubleshooting" className="d-flex align-items-center">
                              <FaExclamationTriangle className="me-2" /> วิธีแก้ปัญหาเบื้องต้น
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link eventKey="contactUs" className="d-flex align-items-center">
                              <FaPhoneAlt className="me-2" /> ติดต่อเรา
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link eventKey="supportRequest" className="d-flex align-items-center">
                              <FaPaperPlane className="me-2" /> แจ้งปัญหาการใช้งาน
                            </Nav.Link>
                          </Nav.Item>
                        </Nav>
                      </Card.Body>
                    </Card>
                  </Col>
                  
                  <Col lg="9" md="8">
                    <Card>
                      <Card.Body>
                        <Tab.Content>
                          {/* User Guide */}
                          <Tab.Pane eventKey="userGuide">
                            <h4 className="mb-4">คู่มือการใช้งาน</h4>
                            
                            {loading ? (
                              <div className="text-center py-5">
                                <Spinner animation="border" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </Spinner>
                              </div>
                            ) : userGuides.length > 0 ? (
                              <Accordion defaultActiveKey="0" className="mb-4">
                                {userGuides.map((guide, index) => (
                                  <Accordion.Item eventKey={index.toString()} key={guide.id}>
                                    <Accordion.Header>{guide.title}</Accordion.Header>
                                    <Accordion.Body>
                                      <div dangerouslySetInnerHTML={{ __html: guide.content }} />
                                    </Accordion.Body>
                                  </Accordion.Item>
                                ))}
                              </Accordion>
                            ) : (
                              <Accordion defaultActiveKey="0" className="mb-4">
                                <Accordion.Item eventKey="0">
                                  <Accordion.Header>การจองคิว Boost Ads</Accordion.Header>
                                  <Accordion.Body>
                                    <ol>
                                      <li>เข้าไปที่เมนู "จองคิว" ในแถบเมนูด้านบน</li>
                                      <li>กรอกข้อมูลในฟอร์มจองคิวให้ครบถ้วน:
                                        <ul>
                                          <li>เลือกเจ้าหน้าที่ที่ต้องการให้ดูแล</li>
                                          <li>เลือกวันและเวลาที่ต้องการจอง</li>
                                          <li>เลือกประเภทบริการ (Boost Live / Post / Consult)</li>
                                          <li>ระบุงบโฆษณา</li>
                                          <li>ระบุความต้องการพิเศษ (ถ้ามี)</li>
                                        </ul>
                                      </li>
                                      <li>กดปุ่ม "ยืนยันจองคิว"</li>
                                      <li>รอการยืนยันจากเจ้าหน้าที่ (สามารถตรวจสอบสถานะได้ที่ตารางประวัติการจอง)</li>
                                    </ol>
                                    <div className="alert alert-info">
                                      <strong>หมายเหตุ:</strong> การจองคิวควรทำล่วงหน้าอย่างน้อย 24 ชั่วโมง เพื่อให้เจ้าหน้าที่มีเวลาเตรียมการ
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                                
                                <Accordion.Item eventKey="1">
                                  <Accordion.Header>การซื้อแพ็คเกจ</Accordion.Header>
                                  <Accordion.Body>
                                    <ol>
                                      <li>เข้าไปที่เมนู "แพ็คเกจ" ในแถบเมนูด้านบน</li>
                                      <li>เลือกแพ็คเกจที่ต้องการซื้อ</li>
                                      <li>กดปุ่ม "ซื้อเลย"</li>
                                      <li>โอนเงินตามจำนวนที่ระบุไปยังบัญชีที่แสดง</li>
                                      <li>อัปโหลดสลิปการโอนเงิน</li>
                                      <li>กดปุ่ม "ยืนยันการสั่งซื้อ"</li>
                                      <li>รอการตรวจสอบจากเจ้าหน้าที่ (สถานะจะเปลี่ยนเป็น "ใช้งานได้" เมื่อตรวจสอบเรียบร้อยแล้ว)</li>
                                    </ol>
                                    <div className="alert alert-info">
                                      <strong>หมายเหตุ:</strong> แพ็คเกจมีอายุการใช้งาน 3 เดือนนับจากวันที่ซื้อ
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                                
                                <Accordion.Item eventKey="2">
                                  <Accordion.Header>การเชื่อมต่อเพจ Facebook</Accordion.Header>
                                  <Accordion.Body>
                                    <ol>
                                      <li>เข้าไปที่หน้า "แดชบอร์ด"</li>
                                      <li>กดปุ่ม "เชื่อมต่อเพจ" ที่อยู่ด้านขวาบน</li>
                                      <li>ล็อกอินเข้าบัญชี Facebook ของคุณ (ถ้ายังไม่ได้ล็อกอิน)</li>
                                      <li>เลือกเพจที่ต้องการเชื่อมต่อ</li>
                                      <li>ยืนยันการอนุญาตให้แอปเข้าถึงข้อมูลเพจ</li>
                                    </ol>
                                    <div className="alert alert-warning">
                                      <strong>สำคัญ:</strong> คุณต้องเป็นแอดมินของเพจที่ต้องการเชื่อมต่อ
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                              </Accordion>
                            )}
                          </Tab.Pane>
                          
                          {/* Troubleshooting */}
                          <Tab.Pane eventKey="troubleshooting">
                            <h4 className="mb-4">วิธีแก้ปัญหาเบื้องต้น</h4>
                            
                            {loading ? (
                              <div className="text-center py-5">
                                <Spinner animation="border" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </Spinner>
                              </div>
                            ) : troubleshootingGuides.length > 0 ? (
                              <Accordion defaultActiveKey="0" className="mb-4">
                                {troubleshootingGuides.map((guide, index) => (
                                  <Accordion.Item eventKey={index.toString()} key={guide.id}>
                                    <Accordion.Header>{guide.title}</Accordion.Header>
                                    <Accordion.Body>
                                      <div dangerouslySetInnerHTML={{ __html: guide.content }} />
                                    </Accordion.Body>
                                  </Accordion.Item>
                                ))}
                              </Accordion>
                            ) : (
                              <Accordion defaultActiveKey="0" className="mb-4">
                                <Accordion.Item eventKey="0">
                                  <Accordion.Header>การเติมเงินในบัญชีโฆษณา</Accordion.Header>
                                  <Accordion.Body>
                                    <h5>วิธีเติมเงินในบัญชีโฆษณา Facebook</h5>
                                    <ol>
                                      <li>เข้าไปที่ <a href="https://business.facebook.com/settings/ad-accounts" target="_blank" rel="noopener noreferrer">Business Manager</a></li>
                                      <li>เลือกบัญชีโฆษณาที่ต้องการเติมเงิน</li>
                                      <li>คลิกที่ "การชำระเงิน"</li>
                                      <li>คลิกที่ "เติมเงิน"</li>
                                      <li>เลือกวิธีการชำระเงินและกรอกข้อมูลให้ครบถ้วน</li>
                                      <li>ยืนยันการชำระเงิน</li>
                                    </ol>
                                    <div className="alert alert-info">
                                      <strong>หมายเหตุ:</strong> หากพบปัญหาในการเติมเงิน สามารถติดต่อเจ้าหน้าที่เพื่อขอความช่วยเหลือได้
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                                
                                <Accordion.Item eventKey="1">
                                  <Accordion.Header>การเคลียร์ละเมิดนโยบายโฆษณา</Accordion.Header>
                                  <Accordion.Body>
                                    <h5>วิธีแก้ไขปัญหาโฆษณาถูกปฏิเสธ</h5>
                                    <ol>
                                      <li>ตรวจสอบอีเมลหรือการแจ้งเตือนจาก Facebook เพื่อดูสาเหตุที่โฆษณาถูกปฏิเสธ</li>
                                      <li>แก้ไขเนื้อหาโฆษณาตามคำแนะนำ:
                                        <ul>
                                          <li>หลีกเลี่ยงการใช้คำที่เกินจริง เช่น "ดีที่สุด" "รักษาได้ทุกโรค"</li>
                                          <li>ไม่ใช้รูปภาพที่มีข้อความเกิน 20% ของพื้นที่</li>
                                          <li>ไม่ใช้รูปภาพที่แสดงผลลัพธ์ก่อน-หลังที่เกินจริง</li>
                                        </ul>
                                      </li>
                                      <li>ส่งโฆษณาเพื่อตรวจสอบอีกครั้ง</li>
                                      <li>หากยังไม่ผ่าน สามารถยื่นอุทธรณ์ได้</li>
                                    </ol>
                                    <div className="alert alert-warning">
                                      <strong>สำคัญ:</strong> การละเมิดนโยบายโฆษณาซ้ำๆ อาจทำให้บัญชีโฆษณาถูกระงับการใช้งาน
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                                
                                <Accordion.Item eventKey="2">
                                  <Accordion.Header>สิ่งที่ควรเลี่ยงในการทำโฆษณา</Accordion.Header>
                                  <Accordion.Body>
                                    <h5>สิ่งที่ควรเลี่ยงในการทำโฆษณา Facebook</h5>
                                    <ul>
                                      <li><strong>ข้อความที่เกินจริง</strong> - หลีกเลี่ยงการใช้คำว่า "ดีที่สุด" "รับประกันผล" "รักษาได้ทุกโรค"</li>
                                      <li><strong>รูปภาพที่มีข้อความมากเกินไป</strong> - Facebook จำกัดข้อความในรูปภาพไม่เกิน 20% ของพื้นที่</li>
                                      <li><strong>การอ้างอิงคุณลักษณะส่วนบุคคล</strong> - เช่น อายุ เพศ รายได้ ศาสนา</li>
                                      <li><strong>การใช้ภาษาที่สร้างความรู้สึกด้อยค่า</strong> - เช่น "คุณอ้วนเกินไปหรือเปล่า?"</li>
                                      <li><strong>การใช้รูปภาพที่แสดงความรุนแรงหรือล่อแหลม</strong></li>
                                      <li><strong>การโฆษณาสินค้าต้องห้าม</strong> - เช่น บุหรี่ อาวุธ ยาที่ไม่ได้รับอนุญาต</li>
                                    </ul>
                                    <div className="alert alert-danger">
                                      <strong>คำเตือน:</strong> การละเมิดนโยบายโฆษณาอย่างร้ายแรงอาจทำให้บัญชีโฆษณาถูกระงับการใช้งานถาวร
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                              </Accordion>
                            )}
                          </Tab.Pane>
                          
                          {/* Contact Us */}
                          <Tab.Pane eventKey="contactUs">
                            <h4 className="mb-4">ติดต่อเรา</h4>
                            
                            {loading ? (
                              <div className="text-center py-5">
                                <Spinner animation="border" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </Spinner>
                              </div>
                            ) : contactInfo ? (
                              <Row>
                                <Col md="6" className="mb-4">
                                  <Card className="h-100">
                                    <Card.Body className="text-center">
                                      <FaPhoneAlt size={40} className="text-primary mb-3" />
                                      <h5>โทรศัพท์</h5>
                                      <p className="mb-0">{contactInfo.phone}</p>
                                      <p className="text-muted">{contactInfo.phoneHours}</p>
                                    </Card.Body>
                                  </Card>
                                </Col>
                                
                                <Col md="6" className="mb-4">
                                  <Card className="h-100">
                                    <Card.Body className="text-center">
                                      <FaLine size={40} className="text-success mb-3" />
                                      <h5>Line Official Account</h5>
                                      <p className="mb-0">{contactInfo.line}</p>
                                      <p className="text-muted">{contactInfo.lineResponse}</p>
                                    </Card.Body>
                                  </Card>
                                </Col>
                                
                                <Col md="6" className="mb-4">
                                  <Card className="h-100">
                                    <Card.Body className="text-center">
                                      <FaFacebook size={40} className="text-primary mb-3" />
                                      <h5>Facebook Page</h5>
                                      <p className="mb-0">{contactInfo.facebook}</p>
                                      <p className="text-muted">{contactInfo.facebookResponse}</p>
                                    </Card.Body>
                                  </Card>
                                </Col>
                                
                                <Col md="6" className="mb-4">
                                  <Card className="h-100">
                                    <Card.Body className="text-center">
                                      <FaGlobe size={40} className="text-info mb-3" />
                                      <h5>เว็บไซต์</h5>
                                      <p className="mb-0">{contactInfo.website}</p>
                                      <p className="text-muted">{contactInfo.websiteDescription}</p>
                                    </Card.Body>
                                  </Card>
                                </Col>
                              </Row>
                            ) : (
                              <Row>
                                <Col md="6" className="mb-4">
                                  <Card className="h-100">
                                    <Card.Body className="text-center">
                                      <FaPhoneAlt size={40} className="text-primary mb-3" />
                                      <h5>โทรศัพท์</h5>
                                      <p className="mb-0">02-123-4567</p>
                                      <p className="text-muted">จันทร์-ศุกร์ 9:00-18:00 น.</p>
                                    </Card.Body>
                                  </Card>
                                </Col>
                                
                                <Col md="6" className="mb-4">
                                  <Card className="h-100">
                                    <Card.Body className="text-center">
                                      <FaLine size={40} className="text-success mb-3" />
                                      <h5>Line Official Account</h5>
                                      <p className="mb-0">@ruaysure</p>
                                      <p className="text-muted">ตอบกลับภายใน 24 ชั่วโมง</p>
                                    </Card.Body>
                                  </Card>
                                </Col>
                                
                                <Col md="6" className="mb-4">
                                  <Card className="h-100">
                                    <Card.Body className="text-center">
                                      <FaFacebook size={40} className="text-primary mb-3" />
                                      <h5>Facebook Page</h5>
                                      <p className="mb-0">facebook.com/ruaysure</p>
                                      <p className="text-muted">ตอบกลับภายใน 24 ชั่วโมง</p>
                                    </Card.Body>
                                  </Card>
                                </Col>
                                
                                <Col md="6" className="mb-4">
                                  <Card className="h-100">
                                    <Card.Body className="text-center">
                                      <FaGlobe size={40} className="text-info mb-3" />
                                      <h5>เว็บไซต์</h5>
                                      <p className="mb-0">www.ruaysure.com</p>
                                      <p className="text-muted">ข้อมูลเพิ่มเติมเกี่ยวกับบริการของเรา</p>
                                    </Card.Body>
                                  </Card>
                                </Col>
                              </Row>
                            )}
                          </Tab.Pane>
                          
                          {/* Support Request */}
                          <Tab.Pane eventKey="supportRequest">
                            <h4 className="mb-4">แจ้งปัญหาการใช้งาน</h4>
                            
                            <Form onSubmit={handleSupportSubmit}>
                              <Row>
                                <Col md="6">
                                  <Form.Group className="mb-3" controlId="formName">
                                    <Form.Label>ชื่อ-นามสกุล</Form.Label>
                                    <Form.Control
                                      type="text"
                                      name="name"
                                      value={supportForm.name}
                                      onChange={handleSupportFormChange}
                                      placeholder="กรอกชื่อ-นามสกุล"
                                      required
                                    />
                                  </Form.Group>
                                </Col>
                                
                                <Col md="6">
                                  <Form.Group className="mb-3" controlId="formEmail">
                                    <Form.Label>อีเมล</Form.Label>
                                    <Form.Control
                                      type="email"
                                      name="email"
                                      value={supportForm.email}
                                      onChange={handleSupportFormChange}
                                      placeholder="กรอกอีเมล"
                                      required
                                    />
                                  </Form.Group>
                                </Col>
                              </Row>
                              
                              <Form.Group className="mb-3" controlId="formSubject">
                                <Form.Label>หัวข้อปัญหา</Form.Label>
                                <Form.Select
                                  name="subject"
                                  value={supportForm.subject}
                                  onChange={handleSupportFormChange}
                                  required
                                >
                                  <option value="">เลือกหัวข้อปัญหา</option>
                                  {supportCategories.length > 0 ? (
                                    supportCategories.map((category) => (
                                      <option key={category.value} value={category.value}>
                                        {category.label}
                                      </option>
                                    ))
                                  ) : (
                                    <>
                                      <option value="account">ปัญหาเกี่ยวกับบัญชีผู้ใช้</option>
                                      <option value="booking">ปัญหาการจองคิว</option>
                                      <option value="package">ปัญหาเกี่ยวกับแพ็คเกจ</option>
                                      <option value="payment">ปัญหาการชำระเงิน</option>
                                      <option value="facebook">ปัญหาเกี่ยวกับ Facebook</option>
                                      <option value="other">อื่นๆ</option>
                                    </>
                                  )}
                                </Form.Select>
                              </Form.Group>
                              
                              <Form.Group className="mb-3" controlId="formMessage">
                                <Form.Label>รายละเอียดปัญหา</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={5}
                                  name="message"
                                  value={supportForm.message}
                                  onChange={handleSupportFormChange}
                                  placeholder="กรอกรายละเอียดปัญหาที่พบ"
                                  required
                                />
                              </Form.Group>
                              
                              <Button variant="primary" type="submit" disabled={submitting}>
                                {submitting ? (
                                  <>
                                    <Spinner
                                      as="span"
                                      animation="border"
                                      size="sm"
                                      role="status"
                                      aria-hidden="true"
                                      className="me-2"
                                    />
                                    กำลังส่ง...
                                  </>
                                ) : (
                                  <>
                                    <FaPaperPlane className="me-2" /> ส่งข้อความ
                                  </>
                                )}
                              </Button>
                            </Form>
                          </Tab.Pane>
                        </Tab.Content>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab.Container>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default HelpCenter;
